/**
 * Permission Level
 */
export type PermissionLevel = 'store' | 'organization';

/**
 * Permission Definition
 *
 * Contains only technical data (backend IDs, level).
 * UI labels (name, description) are managed via i18n in roles.json
 * using the pattern: `permissions.{frontendId}.name` and `permissions.{frontendId}.description`
 *
 * - primaryIds: Backend IDs that define this permission. Match ANY for reading, send ALL for writing.
 *               Supports permission slug migration (old + new slugs coexist).
 * - dependencyIds: Backend IDs required as dependencies. Always sent on write, never used for matching.
 * - requiresEveryPrimaryId: the ids are parts of one capability, not aliases, so some is not enough.
 */
export type Permission = Readonly<{
  frontendId: string;
  primaryIds: readonly string[];
  dependencyIds: readonly string[];
  level: PermissionLevel;
  requiresEveryPrimaryId?: boolean;
}>;

/**
 * Permissions Catalog
 *
 * Complete list of all available permissions in the system.
 * Maps frontend IDs to technical permission definitions.
 *
 * UI translations are in: src/domains/personnel/locales/{lang}/roles.json
 * under the "permissions" key.
 */
export const PERMISSIONS = {
  // ============================================
  // BACKEND-ONLY PERMISSIONS
  // ============================================
  'super-admin': {
    frontendId: 'super-admin',
    primaryIds: ['admin.store'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'organization-admin': {
    frontendId: 'organization-admin',
    primaryIds: ['organization.admin'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'internal-app-version': {
    frontendId: 'internal-app-version',
    primaryIds: ['internal.appVersion'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'internal-force-update': {
    frontendId: 'internal-force-update',
    primaryIds: ['internal.forceUpdateAppVersion'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'internal-promotional-login': {
    frontendId: 'internal-promotional-login',
    primaryIds: ['internal.promotionalLogin'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'parrot-app-access': {
    frontendId: 'parrot-app-access',
    primaryIds: ['parrotApp.access'],
    dependencyIds: [],
    level: 'organization' as const,
  },
  'backoffice-pay-access': {
    frontendId: 'backoffice-pay-access',
    primaryIds: ['backoffice.payAccess'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // ORGANIZATION LEVEL PERMISSIONS
  // ============================================
  'org-gift-certificates': {
    frontendId: 'org-gift-certificates',
    primaryIds: ['admin.canAdminGiftCerticate'],
    dependencyIds: ['backoffice.access'],
    level: 'organization' as const,
  },
  'org-inventory': {
    frontendId: 'org-inventory',
    primaryIds: ['backoffice.moduleInventoryOrganization'],
    dependencyIds: ['backoffice.access'],
    level: 'organization' as const,
  },
  'org-menu': {
    frontendId: 'org-menu',
    primaryIds: ['backoffice.moduleMenu'],
    dependencyIds: ['backoffice.access'],
    level: 'organization' as const,
  },
  'org-personnel': {
    frontendId: 'org-personnel',
    primaryIds: ['admin.modifyEmployeesAndPermissions', 'backoffice.moduleWorkforce'],
    dependencyIds: ['backoffice.access'],
    level: 'organization' as const,
    // One grants the edit rights, the other reveals the section: with either alone it is unreachable.
    requiresEveryPrimaryId: true,
  },

  // ============================================
  // STORE LEVEL - ADMIN
  // ============================================
  'store-admin': {
    frontendId: 'store-admin',
    primaryIds: ['store.admin'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - PORTAL MODULES
  // ============================================
  'store-portal-config': {
    frontendId: 'store-portal-config',
    primaryIds: ['backoffice.moduleSettings', 'admin.typeProductsEditor'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-tickets': {
    frontendId: 'store-portal-tickets',
    primaryIds: ['invoicing.ticketEditor'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-invoicing': {
    frontendId: 'store-portal-invoicing',
    primaryIds: ['backoffice.moduleInvoicing'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-menu': {
    frontendId: 'store-portal-menu',
    primaryIds: ['backoffice.moduleMenu'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-inventory': {
    frontendId: 'store-portal-inventory',
    primaryIds: ['backoffice.moduleInventoryOrganization'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-reports': {
    frontendId: 'store-portal-reports',
    primaryIds: ['backoffice.moduleReports'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },
  'store-portal-subscription': {
    frontendId: 'store-portal-subscription',
    primaryIds: ['backoffice.moduleSubscriptions', 'admin.multistorePayments'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - DEVICES
  // ============================================
  'store-devices-configure': {
    frontendId: 'store-devices-configure',
    primaryIds: ['pos.loginAccess', 'payTerminal.loginAccess'],
    dependencyIds: ['backoffice.access'],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS ACCESS
  // ============================================
  'store-pos-access': {
    frontendId: 'store-pos-access',
    primaryIds: ['pos.pinAccess', 'payTerminal.terminalAccess'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS CASH MODULE
  // ============================================
  'store-pos-cash-module': {
    frontendId: 'store-pos-cash-module',
    primaryIds: ['areaTable.canViewCashFlow'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-cash-open': {
    frontendId: 'store-pos-cash-open',
    primaryIds: ['pos.cashRegisterChangeState'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-cash-deposit': {
    frontendId: 'store-pos-cash-deposit',
    primaryIds: ['pos.cashRegisterDeposit'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-cash-withdraw': {
    frontendId: 'store-pos-cash-withdraw',
    primaryIds: ['pos.cashRegisterWithdrawal'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-cash-tips-report': {
    frontendId: 'store-pos-cash-tips-report',
    primaryIds: ['pos.cashRegisterTipReport'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS CONFIG MODULE
  // ============================================
  'store-pos-config-module': {
    frontendId: 'store-pos-config-module',
    primaryIds: ['areaTable.canViewSettings'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-config-fingerprint': {
    frontendId: 'store-pos-config-fingerprint',
    primaryIds: ['pos.fingerPrintRegistration'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS TABLES MODULE
  // ============================================
  'store-pos-tables-module': {
    frontendId: 'store-pos-tables-module',
    primaryIds: ['areaTable.canViewAreaTables'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-tables-change': {
    frontendId: 'store-pos-tables-change',
    primaryIds: ['areaTable.canChangeTableToOrderAccount'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-tables-change-waiter': {
    frontendId: 'store-pos-tables-change-waiter',
    primaryIds: ['areaTable.canChangeUserToOrderAccount'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-tables-transfer': {
    frontendId: 'store-pos-tables-transfer',
    primaryIds: ['pos.transferItems'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS ORDERS MODULE
  // ============================================
  'store-pos-orders-module': {
    frontendId: 'store-pos-orders-module',
    primaryIds: ['areaTable.canViewOrders'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-orders-force-close': {
    frontendId: 'store-pos-orders-force-close',
    primaryIds: ['pos.forceCloseOrders'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-orders-pause-resume': {
    frontendId: 'store-pos-orders-pause-resume',
    primaryIds: ['pos.manageIntegrations'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS ACCOUNTS
  // ============================================
  'store-pos-accounts-gift-cert': {
    frontendId: 'store-pos-accounts-gift-cert',
    primaryIds: ['pos.canApplyGiftCertificate'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-discounts': {
    frontendId: 'store-pos-accounts-discounts',
    primaryIds: ['pos.applyDiscounts'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-restricted-discounts': {
    frontendId: 'store-pos-accounts-restricted-discounts',
    primaryIds: ['pos.applyRestrictedDiscounts'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-auto-logout': {
    frontendId: 'store-pos-accounts-auto-logout',
    primaryIds: ['end_session_kitchen'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-create': {
    frontendId: 'store-pos-accounts-create',
    primaryIds: ['pos.orderCreate'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-edit-after-print': {
    frontendId: 'store-pos-accounts-edit-after-print',
    primaryIds: ['pos.orderEdit', 'pos.orderReOpen'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-reprint-order': {
    frontendId: 'store-pos-accounts-reprint-order',
    primaryIds: ['pos.reprintCommand'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-reprint-receipt': {
    frontendId: 'store-pos-accounts-reprint-receipt',
    primaryIds: ['pos.reprintTicket'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-accounts-view-others': {
    frontendId: 'store-pos-accounts-view-others',
    primaryIds: ['pos.seeOrdersFromOtherUsers', 'payTerminal.canViewAllTransactions', 'areaTable.canViewOrdersAccount'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS USERS
  // ============================================
  'store-pos-users-logout-all': {
    frontendId: 'store-pos-users-logout-all',
    primaryIds: ['pos.forceCheckoutTimeAllUsers'],
    dependencyIds: [],
    level: 'store' as const,
  },

  // ============================================
  // STORE LEVEL - POS PAYMENTS
  // ============================================
  'store-pos-payments-update': {
    frontendId: 'store-pos-payments-update',
    primaryIds: ['pos.updatePayment'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-cancel-items': {
    frontendId: 'store-pos-payments-cancel-items',
    primaryIds: ['pos.voidItems'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-cancel-service-charge': {
    frontendId: 'store-pos-payments-cancel-service-charge',
    primaryIds: ['pos.voidServiceCharge'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-cancel-account': {
    frontendId: 'store-pos-payments-cancel-account',
    primaryIds: ['pos.voidOrders'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-cancel-payment': {
    frontendId: 'store-pos-payments-cancel-payment',
    primaryIds: ['pos.voidPayment'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-edit-closed': {
    frontendId: 'store-pos-payments-edit-closed',
    primaryIds: ['pos.editePaymentCloseOrders'],
    dependencyIds: [],
    level: 'store' as const,
  },
  'store-pos-payments-register': {
    frontendId: 'store-pos-payments-register',
    primaryIds: ['pos.createPayment'],
    dependencyIds: [],
    level: 'store' as const,
  },
} as const;

/**
 * Frontend Permission ID type
 */
export type FrontendPermissionId = keyof typeof PERMISSIONS;
